import React, { useState } from 'react';
import { validateEmail, generateEmailLabel } from '../../utils/email';
import { PlusIcon } from '@heroicons/react/24/outline';

interface AddEmailFormProps {
  onAdd: (email: string) => Promise<any>;
  workExperiences?: Array<{ id: string; company: string; position: string; }>;
  isLoading?: boolean;
  canAddMore?: boolean;
}

export function AddEmailForm({ onAdd, workExperiences = [], isLoading, canAddMore = true }: AddEmailFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [workExperienceId, setWorkExperienceId] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAdd(email);
      // Reset form on success
      setEmail('');
      setLabel('');
      setWorkExperienceId('');
      setIsOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Auto-suggest label if none set
    if (!label && newEmail.includes('@')) {
      setLabel(generateEmailLabel(newEmail));
    }
  };

  if (!isOpen) {
    if (!canAddMore) {
      return (
        <div className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 cursor-not-allowed">
          <PlusIcon className="h-4 w-4 mr-2" />
          Maximum Email Addresses Reached
        </div>
      );
    }
    
    return (
      <button
        onClick={() => setIsOpen(true)}
        disabled={isLoading || !canAddMore}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-600"
      >
        <PlusIcon className="h-4 w-4 mr-2" />
        Add Email Address
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            placeholder="work@company.com"
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="label" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Label (optional)
          </label>
          <select
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          >
            <option value="">Select a label</option>
            <option value="Work">Work</option>
            <option value="Personal">Personal</option>
            <option value="School">School</option>
            <option value="Project">Project</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {workExperiences.length > 0 && (
          <div>
            <label htmlFor="workExperience" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Link to Work Experience (optional)
            </label>
            <select
              id="workExperience"
              value={workExperienceId}
              onChange={(e) => setWorkExperienceId(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="">None</option>
              {workExperiences.map((exp) => (
                <option key={exp.id} value={exp.id}>
                  {exp.position} at {exp.company}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Notes sent from this email will be automatically linked to this work experience.
            </p>
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            We'll send a verification email to this address. Once verified, you can send emails to <span className="font-mono bg-blue-100 dark:bg-blue-800 px-1 rounded">notes@careercard.ai</span> from this address to create notes.
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
            Note: Each email address can send up to 5 notes per day. You can register up to 5 email addresses total.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setEmail('');
              setLabel('');
              setWorkExperienceId('');
              setError('');
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            {isSubmitting ? 'Adding...' : 'Add Email'}
          </button>
        </div>
      </form>
    </div>
  );
}